import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  Typography,
  Divider,
  CircularProgress,
  Dialog,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { UploadOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";
import axios from "axios";
import { URL } from "../config";


const formatDateForInput = (date) => {
  if (!date) return "";
  return new Date(date).toISOString().split("T")[0];
};
const today = new Date().toISOString().split("T")[0];


const AddOffersModal = ({ onClose, offer, refresh }) => {
  const isEdit = Boolean(offer);
  const [loading, setLoading] = useState(false);

  const [companyLogo, setCompanyLogo] = useState(null);
  const [offerImage, setOfferImage] = useState(null);
  const [companyLogoPreview, setCompanyLogoPreview] = useState("");
  const [offerImagePreview, setOfferImagePreview] = useState("");

  const [offerTypes, setOfferTypes] = useState([]);
  const [selectedOfferTypeId, setSelectedOfferTypeId] = useState(null);

  const [formData, setFormData] = useState({
    companyName: "",
    companyUrl: "",          
    offerTitle: "",
    shortDescription: "",
    couponCode: "",
    offerDetails: "",
    startdate: "",
    endDate: "",
  });

  useEffect(() => {
    axios
      .get(`${URL}/offertypes`)
      .then((res) => setOfferTypes(res.data))
      .catch((err) => console.error(err));
  }, []);

 
  useEffect(() => {
    if (offer) {
      setFormData({
        companyName: offer.companyName || "",
        companyUrl: offer.companyUrl || "", 
        offerTitle: offer.offerTitle || "",
        shortDescription: offer.shortDescription || "",
        couponCode: offer.couponCode || "",
        offerDetails: offer.offerDetails || "",
        startdate: formatDateForInput(offer.startdate),
        endDate: formatDateForInput(offer.endDate),
      });

      setCompanyLogoPreview(
        offer.companyLogo ? `${URL}${offer.companyLogo}` : ""
      );
      setOfferImagePreview(
        offer.offerImage ? `${URL}${offer.offerImage}` : ""
      );

      setSelectedOfferTypeId(offer.fkofferTypeId || null);
    }
  }, [offer]);

  const handleChange = (key, value) =>
    setFormData({ ...formData, [key]: value });

  const validateImage = (file) => {
    if (!file?.type.startsWith("image/")) {
      toast.error("Only image files allowed");
      return false;
    }
    return true;
  };

  const handleCompanyLogo = (e) => {
    const file = e.target.files[0];
    if (!validateImage(file)) return;
    setCompanyLogo(file);
    setCompanyLogoPreview(window.URL.createObjectURL(file));
  };

  const handleOfferImage = (e) => {
    const file = e.target.files[0];
    if (!validateImage(file)) return;
    setOfferImage(file);
    setOfferImagePreview(window.URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!formData.offerTitle) {
      toast.error("Offer title is required");
      return;
    }
    if (!selectedOfferTypeId) {
      toast.error("Please select an offer type");
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([k, v]) =>
        payload.append(k, v ?? "")
      );

      payload.append("fkofferTypeId", selectedOfferTypeId);

      if (companyLogo) payload.append("companyLogo", companyLogo);
      if (offerImage) payload.append("offerImage", offerImage);

      if (isEdit) {
        await axios.patch(`${URL}/offers/${offer.offerId}`, payload);
        toast.success("Offer updated successfully");
      } else {
        await axios.post(`${URL}/offers`, payload);
        toast.success("Offer added successfully");
      }

      refresh?.();
      onClose?.();
    } catch {
      toast.error("Failed to save offer");
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = {
    "& .MuiOutlinedInput-root": {
      minHeight: "28px",
      fontSize: "14px",
      "& fieldset": { borderColor: "#c4c4c4" },
      "&:hover fieldset": { borderColor: "#184459ff" },
      "&.Mui-focused fieldset": {
        borderColor: "#c4c4c4",
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "18px",
      color: "#184459ff",
      fontWeight: 600,
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#184459ff" },
  };
useEffect(() => {
  if (
    formData.startdate &&
    formData.endDate &&
    formData.endDate < formData.startdate
  ) {
    handleChange("endDate", "");
  }
}, [formData.startdate]);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: "90%", sm: 450 },
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
      BackdropProps={{
        sx: { backgroundColor: "rgba(0,0,0,0.4)" },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" textAlign="center" sx={{ color: "#184459ff" }}>
            {isEdit ? "Edit Offer" : "Add Offer"}
          </Typography>
          <Divider sx={{ mt: 1 }} />
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Company Name"
              size="small"
              fullWidth
              value={formData.companyName}
              onChange={(e) => handleChange("companyName", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fieldStyle}
            />

            <TextField
              label="Company URL"
              size="small"
              fullWidth
              value={formData.companyUrl}        
              onChange={(e) => handleChange("companyUrl", e.target.value)} 
              InputLabelProps={{ shrink: true }}
              sx={fieldStyle}
            />

            <UploadBox
              label="Upload Company Logo"
              onChange={handleCompanyLogo}
              preview={companyLogoPreview}
            />

            <UploadBox
              label="Upload Offer Image"
              onChange={handleOfferImage}
              preview={offerImagePreview}
            />

            <TextField
              label="Offer Title *"
              size="small"
              fullWidth
              value={formData.offerTitle}
              onChange={(e) => handleChange("offerTitle", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fieldStyle}
            />

            <TextField
              label="Short Description"
              size="small"
              fullWidth
              value={formData.shortDescription}
              onChange={(e) => handleChange("shortDescription", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fieldStyle}
            />

            <TextField
              label="Coupon Code"
              size="small"
              fullWidth
              value={formData.couponCode}
              onChange={(e) => handleChange("couponCode", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fieldStyle}
            />

            <TextField
              label="Offer Details"
              multiline
              rows={3}
              size="small"
              fullWidth
              value={formData.offerDetails}
              onChange={(e) => handleChange("offerDetails", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fieldStyle}
            />

           <TextField
  type="date"
  label="Start Date"
  size="small"
  fullWidth
  value={formData.startdate}
  onChange={(e) => handleChange("startdate", e.target.value)}
  InputLabelProps={{ shrink: true }}
  inputProps={{
    min: today, 
  }}
  sx={fieldStyle}
/>
<TextField
  type="date"
  label="End Date"
  size="small"
  fullWidth
  value={formData.endDate}
  onChange={(e) => handleChange("endDate", e.target.value)}
  InputLabelProps={{ shrink: true }}
  inputProps={{
    min: formData.startdate || today, 
  }}
  sx={fieldStyle}
/>

          
            <FormControl
              sx={{
                border: "1px solid #c4c4c4",
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <FormLabel
                sx={{
                  fontSize: "14px",
                  color: "#184459ff",
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                Offer Type
              </FormLabel>

              <RadioGroup
                value={selectedOfferTypeId}
                onChange={(e) => setSelectedOfferTypeId(Number(e.target.value))}
              >
                <Stack spacing={1}>
                  {offerTypes.map((type) => (
                    <FormControlLabel
                      key={type.offerTypeId}
                      value={type.offerTypeId}
                      control={
                        <Radio
                          sx={{
                            color: "#184459ff",
                            "&.Mui-checked": { color: "#184459ff" },
                            padding: 0.5,
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: 14, color: "#174253" }}>
                          {type.offerTypeName}
                        </Typography>
                      }
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: 14,
                        },
                      }}
                    />
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>
          </Stack>
        </Box>

        <Box sx={{ p: 3, borderTop: "1px solid #c4c4c4" }}>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#184459ff" }}
              onClick={handleSave}
              startIcon={loading && <CircularProgress size={18} />}
            >
              {loading ? "Saving..." : isEdit ? "Update Offer" : "Add Offer"}
            </Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#757575ff" }}
              onClick={onClose}
            >
              Close
            </Button>
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
};


const UploadBox = ({ label, onChange, preview }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      border: "1px solid #c4c4c4",
      p: 1.5,
      borderRadius: 2,
    }}
  >
    <Button component="label" startIcon={<UploadOutlined />}>
      {label}
      <input hidden type="file" accept="image/*" onChange={onChange} />
    </Button>
    {preview && (
      <img
        src={preview}
        alt="preview"
        style={{ width: 50, height: 50, borderRadius: 8 }}
      />
    )}
  </Box>
);

export default AddOffersModal;
